import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useFetch } from "hooks";
import { get } from "utils/api";
import { message, errorMessage } from "actions/message";
import SearchableDropdown from "components/searchable-dropdown";
import FormikField from "components/forms/formik-field";
import { Formik } from "formik";
import PhoneNumberField from "components/forms/phone-number-field";
import { formatPhone } from "utils/misc";
import SynthpopPayloadViewer from "./synthpop-payload-viewer";
import formatTranscript from "./format-transcript";

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: "white",
  padding: "20px",
  borderRadius: "8px",
  width: "90vw",
  maxWidth: "700px",
  maxHeight: "85vh",
  overflow: "auto",
  boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "15px",
  borderBottom: "1px solid #eee",
  paddingBottom: "10px"
};

const modalCloseButtonStyle = {
  cursor: "pointer",
  border: "none",
  background: "#4281A6",
  color: "white",
  borderRadius: "4px",
  padding: "6px 14px",
  fontSize: "14px",
  fontWeight: "500"
};

const TranscriptModal = ({ transcript, onClose }) => (
  <div style={modalOverlayStyle} onClick={onClose}>
    <div style={{ ...modalContentStyle, height: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
      <div style={{ ...modalHeaderStyle, flexShrink: 0 }}>
        <h3 style={{ margin: 0 }}>Call Transcript</h3>
        <button onClick={onClose} style={modalCloseButtonStyle}>Close</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", lineHeight: "1.5" }}>
        {formatTranscript(transcript)}
      </div>
    </div>
  </div>
);

const RawPayloadModal = ({ payload, onClose }) => (
  <div style={modalOverlayStyle} onClick={onClose}>
    <div style={{ ...modalContentStyle, width: "90vw", maxWidth: "1100px", height: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
      <div style={{ ...modalHeaderStyle, flexShrink: 0 }}>
        <h3 style={{ margin: 0 }}>Synthpop Raw Payload</h3>
        <button onClick={onClose} style={modalCloseButtonStyle}>Close</button>
      </div>
      <SynthpopPayloadViewer payload={payload} />
    </div>
  </div>
);

TranscriptModal.propTypes = {
  transcript: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

RawPayloadModal.propTypes = {
  payload: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  onClose: PropTypes.func.isRequired
};

const SynthpopTesting = () => {
  const dispatch = useDispatch();

  const initiateSynthpopCallApi = useFetch({
    apiFn: patientId =>
      get(`synthpop/initiate-call/${patientId}`).then(x => setCallSid(x))
  });

  const fetchSynthpopTaskApi = useFetch({
    apiFn: ({patientId, processOutcome}) =>
      get(`synthpop/fetch-task-details/${patientId}/${processOutcome}`).then(x => {
        setTaskInfo(x);
        setShowTranscript(false);
        setShowRawPayload(false);
        if (x?.message) {
          dispatch(message(x.message));
        }
      })
  });

  const [usePhoneSearch, setUsePhoneSearch] = React.useState(true);
  const [manualGuid, setManualGuid] = React.useState("");
  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const [patients, setPatients] = React.useState([]);
  const [callSid, setCallSid] = React.useState(null);
  const [fetchCallSid, setFetchCallSid] = React.useState("");
  const [taskInfo, setTaskInfo] = React.useState(null);
  const [processOutcome, setProcessOutcome] = React.useState(false);
  const [showTranscript, setShowTranscript] = React.useState(false);
  const [showRawPayload, setShowRawPayload] = React.useState(false);

  const getPatients = async e => {
    dispatch(message("Getting patients..."));
    try {
      const response = await get(`patients/ivr_test_patients/${e.phone}/Agent`);
      if (response && response.length > 0) {
        setSelectedPatient(response[0].patient_guid);
        dispatch(
          message(
            `Found ${response.length} patient${response.length === 1 ? "" : "s"}`
          )
        );
        setPatients(response);
      } else {
        dispatch(message("No patients found for this phone number"));
        setPatients([]);
        setSelectedPatient(null);
      }
    } catch (error) {
      dispatch(errorMessage("Error searching for patients"));
      setPatients([]);
      setSelectedPatient(null);
    }
  };


  return (
    <div>
      <h1>Synthpop Integration Testing</h1>

      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="radio"
            checked={usePhoneSearch}
            onChange={() => {
              setUsePhoneSearch(true);
              setManualGuid("");
              setSelectedPatient(null);
            }}
            style={{ marginRight: '8px' }}
          />
          Search by Phone Number
        </label>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="radio"
            checked={!usePhoneSearch}
            onChange={() => {
              setUsePhoneSearch(false);
              setPatients([]);
              setSelectedPatient(null);
            }}
            style={{ marginRight: '8px' }}
          />
          Use Patient GUID
        </label>
      </div>

      {usePhoneSearch ? (
        <div>
          <h3>Search Patient by Phone Number</h3>
        <Formik
          onSubmit={(values) => {
            if (values.phone.length > 10) {
              dispatch(message("Phone must be shorter than 10 numbers"));
            } else {
              getPatients(values);
            }
          }}
          initialValues={{ phone: "" }}
        >
          {({ values }) => (
            <form>
              <FormikField
                name="phone"
                label="Phone Number:"
                type="tel"
                component={PhoneNumberField}
                placeholder="Search Patient By Phone Number"
              />
              <button
                disabled={values.phone.length < 10}
                onClick={() => {
                  getPatients(values);
                }}
                type="button"
              >
                Search
              </button>
            </form>
          )}
        </Formik>

        <div>
          Patients Found:
          <SearchableDropdown
            options={patients.map((x, i) => {
              return {
                text:
                  x.company_name +
                  " calling from " +
                  formatPhone(x.outbound_ivr_number || "N/A") +
                  " to " +
                  x.first_name +
                  " " +
                  x.last_name +
                  " at " +
                  formatPhone(x.patient_phone),
                value: x.patient_guid,
                key: x.patient_guid + i
              };
            })}
            selected={selectedPatient}
            onSelect={e => {
              setSelectedPatient(e);
            }}
          />
          {selectedPatient && patients.find(p => p.patient_guid === selectedPatient)?.patient_secondary_phone && (
            <div style={{ color: 'red', marginTop: '8px' }}>
              This patient has a secondary phone number: {patients.find(p => p.patient_guid === selectedPatient).patient_secondary_phone}. If the first call goes to voicemail, the secondary number will be called.
            </div>
          )}
        </div>
        </div>
      ) : (
        <div>
          <h3>Enter Patient GUID</h3>
          <input
            type="text"
            placeholder="Patient GUID"
            value={manualGuid}
            onChange={e => setManualGuid(e.target.value)}
            style={{ padding: '8px', marginRight: '10px', width: '300px' }}
          />
        </div>
      )}

      <div>
        <button
          onClick={() => initiateSynthpopCallApi.fetchData(usePhoneSearch ? selectedPatient : manualGuid)}
          disabled={usePhoneSearch ? !selectedPatient : !manualGuid}
        >
          Start Agent Call
        </button>
        {callSid ? <div>Call sid: {JSON.stringify(callSid)}</div> : ""}
      </div>

      <div>
        <h3>Fetch Synthpop Task</h3>
        <input
          type="text"
          placeholder="Call SID or Synthpop UUID"
          value={fetchCallSid}
          onChange={e => setFetchCallSid(e.target.value)}
          style={{ width: '350px' }}
        />
         <label>
          <input
            type="checkbox"
            checked={processOutcome}
            onChange={e => setProcessOutcome(e.target.checked)}
          />
          Process Outcome
        </label>
        <button
          onClick={() => fetchSynthpopTaskApi.fetchData({
            patientId: fetchCallSid || (usePhoneSearch ? selectedPatient : manualGuid),
            processOutcome: processOutcome
          })}
          disabled={!fetchCallSid && !selectedPatient && !manualGuid}
        >
          Submit
        </button>
        {taskInfo ? (
              <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
            <div style={{ marginTop: '10px' }}>
              <strong>Task Status:</strong> {taskInfo.taskStatus}
            </div>
            {taskInfo.synthpopUUID && (
              <div style={{ marginTop: '10px' }}>
                <strong>Synthpop UUID:</strong>{' '}
                <span style={{ fontFamily: 'monospace', color: '#9c27b0' }}>{taskInfo.synthpopUUID}</span>
              </div>
            )}
            {taskInfo.outcome && (
              <div style={{ marginTop: '10px' }}>
                <strong>Outcome:</strong> {taskInfo.outcome}
              </div>
            )}
            {taskInfo.outcomeProcessed && (
              <div style={{ marginTop: '10px' }}>
                <strong>Outcome Processed:</strong> Yes
              </div>
            )}
            {taskInfo.orderCreated && (
              <div style={{ marginTop: '10px' }}>
                <strong>Order Created:</strong> Yes
              </div>
            )}
            {taskInfo.orderGuid && (
              <div style={{ marginTop: '10px' }}>
                <strong>Order:</strong>{' '}
                <Link to={`/orders/${taskInfo.orderGuid}`}>
                  View Order
                </Link>
              </div>
            )}
            {taskInfo.recordingUrl && (
              <div style={{ marginTop: '10px' }}>
                <strong>Recording:</strong>{' '}
                <a href={taskInfo.recordingUrl} target="_blank" rel="noopener noreferrer">
                  Listen to Recording
                </a>
              </div>
            )}
            <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
              {taskInfo.transcript && (
                <button
                  type="button"
                  onClick={() => setShowTranscript(true)}
                  style={{ cursor: 'pointer', padding: '3px 8px', fontSize: '12px', backgroundColor: '#4281A6', color: 'white', border: 'none', borderRadius: '3px' }}
                >
                  View Transcript
                </button>
              )}
              {taskInfo.rawPayload && (
                <button
                  type="button"
                  onClick={() => setShowRawPayload(true)}
                  style={{ cursor: 'pointer', padding: '3px 8px', fontSize: '12px', backgroundColor: '#9c27b0', color: 'white', border: 'none', borderRadius: '3px' }}
                >
                  View Raw Payload
                </button>
              )}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>

      {/* Modals */}
      {showTranscript && taskInfo?.transcript && (
        <TranscriptModal
          transcript={taskInfo.transcript}
          onClose={() => setShowTranscript(false)}
        />
      )}
      {showRawPayload && taskInfo?.rawPayload && (
        <RawPayloadModal
          payload={taskInfo.rawPayload}
          onClose={() => setShowRawPayload(false)}
        />
      )}
    </div>
  );
};

export default SynthpopTesting;
